// client/src/hooks/useLikes.js
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext"; // Assuming AuthContext is here
import * as likeApi from "../utils/likeApi"; // Import your like API functions

/**
 * Custom React hook for managing like status and actions for a specific item.
 * @param {string} itemId The ID of the item to like/unlike.
 * @returns {object} An object containing:
 * - `isLiked`: A boolean indicating if the item is currently liked by the user.
 * - `likeCount`: The number of likes for this item.
 * - `toggleLike`: An async function to toggle the like status.
 * - `likeLoading`: A boolean indicating if a like/unlike action is in progress.
 * - `likeError`: An error message if a like/unlike action fails.
 */
export const useLikes = (itemId) => {
  const {
    userObjectId,
    currentUser,
    loading: authLoading,
    backendDataLoading: authBackendLoading,
  } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(true); // Initial loading for checking like status
  const [likeError, setLikeError] = useState(null);

  // Function to fetch the total like count for the item
  const fetchLikeCount = useCallback(async () => {
    if (!itemId) {
      console.log("useLikes: Skipping like count fetch. Item ID is missing.");
      setLikeCount(0);
      return;
    }
    try {
      const count = await likeApi.getItemLikeCount(itemId);
      setLikeCount(count);
      console.log(`useLikes: Fetched like count for item ${itemId}: ${count}`);
    } catch (error) {
      console.error(
        `useLikes: Error fetching like count for item ${itemId}:`,
        error
      );
      setLikeError(error.message || "Failed to fetch like count.");
      setLikeCount(0); // Default to 0 on error
    }
  }, [itemId]);

  // Function to check if the item is liked by the current user
  const checkLikeStatus = useCallback(async () => {
    setLikeError(null); // Clear any previous errors

    // Only check if liked if we have auth data and item ID
    if (authLoading || authBackendLoading || !userObjectId || !itemId) {
      console.log(
        `useLikes: Skipping like status check. Auth loading: ${authLoading}, Backend loading: ${authBackendLoading}, User ID: ${!!userObjectId}, Item ID: ${!!itemId}`
      );
      setIsLiked(false); // Default to not liked if conditions aren't met
      setLikeLoading(false); // Stop loading if we can't check
      return;
    }

    setLikeLoading(true); // Start loading when attempting to check status
    try {
      const liked = await likeApi.checkItemLiked(userObjectId, itemId);
      setIsLiked(liked);
      console.log(
        `useLikes: Checked like status for item ${itemId}. Is liked: ${liked}`
      );
    } catch (error) {
      console.error(
        `useLikes: Error checking like status for item ${itemId}:`,
        error
      );
      setLikeError(error.message || "Failed to check like status.");
      setIsLiked(false); // Assume not liked on error
    } finally {
      setLikeLoading(false); // Always set loading to false after check
    }
  }, [userObjectId, itemId, authLoading, authBackendLoading]);

  // Effect to run the initial checks and re-check when dependencies change
  useEffect(() => {
    checkLikeStatus();
    fetchLikeCount(); // Fetch the like count when component mounts or item ID changes
  }, [checkLikeStatus, fetchLikeCount]); // Dependencies are the memoized functions

  // Function to toggle the like status
  const toggleLike = useCallback(async () => {
    if (
      likeLoading ||
      authLoading ||
      authBackendLoading ||
      !userObjectId ||
      !itemId
    ) {
      console.warn(
        "useLikes: Cannot toggle like while loading or missing user/item ID."
      );
      return;
    }

    setLikeError(null); // Clear previous errors
    setLikeLoading(true); // Start loading for the toggle action

    // Optimistically update UI
    const wasLiked = isLiked;
    // Update likeCount based on the optimistic update
    const newLikeCount = wasLiked ? likeCount - 1 : likeCount + 1;
    setIsLiked(!wasLiked);
    setLikeCount(newLikeCount);

    try {
      let success = false;
      if (wasLiked) {
        success = await likeApi.unlikeItem(userObjectId, itemId);
        console.log(`useLikes: Unliked item ${itemId}. Success: ${success}`);
      } else {
        success = await likeApi.likeItem(userObjectId, itemId);
        console.log(`useLikes: Liked item ${itemId}. Success: ${success}`);
      }

      if (!success) {
        // Revert optimistic update if API indicates failure
        setIsLiked(wasLiked);
        setLikeCount(likeCount); // Revert to original count
        setLikeError("Failed to update like status due to API response.");
      }
      // After a successful toggle, re-fetch the accurate count to ensure consistency
      // This is important because other users might have liked/unliked in the interim
      await fetchLikeCount();
    } catch (error) {
      console.error(`useLikes: Error toggling like for item ${itemId}:`, error);
      setLikeError(error.message || "Failed to update like status.");
      // Revert optimistic update on error
      setIsLiked(wasLiked);
      setLikeCount(likeCount); // Revert to original count
    } finally {
      setLikeLoading(false); // Always set loading to false after toggle
    }
  }, [
    isLiked,
    likeCount,
    userObjectId,
    itemId,
    likeLoading,
    authLoading,
    authBackendLoading,
    fetchLikeCount, // Add fetchLikeCount to dependencies
  ]);

  return {
    isLiked,
    likeCount,
    toggleLike,
    likeLoading,
    likeError,
  };
};
